/**
 * The arithmetic behind the crop editor (HAT-1302): turning what the office marked on each page into the
 * instructions the server carries out, and answering the one question the confirm button depends on -
 * whether anything would be left of the document afterwards.
 *
 * The drag itself is the same arithmetic the redaction editor already does, so it is borrowed rather than
 * written twice; a rectangle on a scaled-down page is a rectangle either way.
 */

import { boxFromDrag, boxToPixels, isMeaningfulBox, type RedactionBox } from './redactionGeometry';

export { boxFromDrag, boxToPixels, isMeaningfulBox };
export type { Point, Size } from './redactionGeometry';

/** The part of a page that survives, as fractions of the page from its top-left corner. */
export type CropBox = RedactionBox;

/** What the office decided about one page. */
export interface CropPageState {
  /** The whole page goes - the appendix photographed onto a page of its own. */
  remove?: boolean;
  /** The rectangle to keep. Undefined keeps the page whole. */
  keep?: CropBox;
}

/** One page's instruction, in the shape the server is sent. */
export interface CropPageInstruction {
  page: number;
  remove: boolean;
  keep?: CropBox;
}

/**
 * What the office actually changed, as the server wants it: the pages it marked, in page order.
 *
 * A page it paged past and left alone is left out rather than sent as "keep whole". The server treats an
 * omission as "nothing happens here", and that has to stay the meaning of saying nothing - the mistake
 * this cannot recover from is throwing away a page nobody meant to touch.
 */
export const cropInstructions = (byPage: Record<number, CropPageState>): CropPageInstruction[] =>
  Object.entries(byPage)
    .map(([page, state]) => ({ page: Number(page), remove: !!state.remove, keep: state.keep }))
    .filter((entry) => entry.remove || !!entry.keep)
    .map((entry) => (entry.remove ? { page: entry.page, remove: true } : entry))
    .sort((left, right) => left.page - right.page);

/**
 * How many pages the document would still have. The confirm button is off while this is zero: a document
 * with no pages is not a file that can replace anything, and the ID card has to stay in the fund's record.
 */
export const survivingPageCount = (byPage: Record<number, CropPageState>, pageCount: number): number =>
  pageCount - cropInstructions(byPage).filter((entry) => entry.remove).length;

/**
 * A short account of what is about to happen, for the confirmation to say out loud. The office is about to
 * destroy something it cannot get back, so it should read what it marked rather than remember it.
 */
export const describeCrop = (byPage: Record<number, CropPageState>, pageCount: number): string => {
  const instructions = cropInstructions(byPage);
  const removed = instructions.filter((entry) => entry.remove).map((entry) => entry.page + 1);
  const cropped = instructions.filter((entry) => !entry.remove).map((entry) => entry.page + 1);

  const parts: string[] = [];
  if (cropped.length > 0) {
    parts.push(
      pageCount > 1
        ? `${cropped.length === 1 ? 'עמוד' : 'עמודים'} ${cropped.join(', ')} ${cropped.length === 1 ? 'ייחתך' : 'ייחתכו'} לאזור שסומן`
        : 'הקובץ ייחתך לאזור שסומן'
    );
  }

  if (removed.length > 0) {
    parts.push(
      `${removed.length === 1 ? 'עמוד' : 'עמודים'} ${removed.join(', ')} ${removed.length === 1 ? 'יוסר' : 'יוסרו'} מהקובץ`
    );
  }

  return parts.join('; ');
};
